// Components==============
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import storeWrongAnswer from 'actions/wrongAnswer/store';
import ArrowButton from 'components/UI/ArrowButton/ArrowButton';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { AudioExerciseContext } from 'screens/AudioExercise/AudioExercise';
import { wiggle } from 'utilities/framerAnimations';
import { useAudio } from 'utilities/useAudio';
import { FindWrongContext } from '../../FindWrongExercise';
import './Option.module.scss';
// =========================

type Status = 'initial' | 'playing' | 'played';

const variants = {
    initial: {
        scale: 1,
        rotate: 0,
    },
    playing: {
        rotate: wiggle,
        transition: {
            duration: 0.8,
            yoyo: Infinity,
        },
    },
    played: {
        rotate: 0,
    },
};

export default function Option({
    isCorrect,
    option,
}: {
    isCorrect: boolean;
    option: {
        name: string;
        audio: string;
    };
}) {
    const { background } = useContext(AudioExerciseContext);

    const {
        userCanChoose,
        setPlayButtonsClicked,
        setUserAnswers,
        current,
        currentAudio,
        wrongAnswer,
    } = useContext(FindWrongContext);

    const [status, setStatus] = useState<Status>('initial');
    const [answerIsCorrect, setAnswerIsCorrect] = useState(false);
    const [isAnswered, setIsAnswered] = useState(false);
    const [triggerAnimation, setTriggerAnimation] = useState(false);

    const dispatch = useDispatch();

    const { treatmentId, exerciseId } = useParams() as {
        treatmentId: string;
        exerciseId: string;
    };

    function storeWrong() {
        dispatch(
            storeWrongAnswer({
                given_answer: option.name,
                correct_answer: wrongAnswer.name,
                type: 'woord',
                exercise_id: exerciseId,
                exercise_type: 'audio',
                treatment_id: treatmentId,
            })
        );
    }

    function handleClick() {
        if (isCorrect && option?.name) {
            setAnswerIsCorrect(true);
            setUserAnswers((prev) => [...prev, option?.name]);
        } else {
            setTriggerAnimation(true);
            setIsAnswered(true);
            storeWrong();
        }
    }

    useEffect(() => {
        setTimeout(() => {
            setTriggerAnimation(false);
        }, 1000);
    }, [triggerAnimation]);

    useEffect(() => {
        setStatus('initial');
        setAnswerIsCorrect(false);
        setIsAnswered(false);
    }, [current]);

    const { play } = useAudio({
        audioFile: option?.audio ? `/storage/${option.audio}` : undefined,
    });

    useEffect(() => {
        if (currentAudio === option?.name) {
            setStatus('playing');
            play();
            setTimeout(() => {
                setStatus('played');
            }, 1000);
        }
    }, [currentAudio, play]);

    return (
        <motion.div
            styleName="button-wrapper"
            animate={
                triggerAnimation ? { x: [0, 10, -8, 8, -10, 0] } : { x: 0 }
            }
            initial={{ x: 0 }}
        >
            <motion.div
                onClick={() => {
                    if (status === 'initial') {
                        setPlayButtonsClicked((prev) => prev + 1);
                    }
                    play();
                    setStatus('playing');
                    setTimeout(() => {
                        setStatus('played');
                    }, 1000);
                }}
                styleName={`wrapper ${status} `}
                initial="initial"
                animate={status}
                variants={variants}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                style={{ background: background === 'desert' ? '#AD9993' : '' }}
            >
                <FontAwesomeIcon icon={faPlay} />
            </motion.div>
            <AnimatePresence>
                {userCanChoose && (
                    <ArrowButton
                        handleClick={handleClick}
                        answerIsCorrect={answerIsCorrect}
                        isAnswered={isAnswered}
                        background={background}
                    />
                )}
            </AnimatePresence>
        </motion.div>
    );
}
